// src/components/OrchestratedProposalGenerator.tsx
// Orchestrated proposal generator with gap analysis and progress tracking

import React, { useState, useEffect, useCallback } from 'react';
import {
  X,
  ArrowLeft,
  Sparkles,
  AlertCircle,
  Clock,
  Minus,
  Maximize2,
  Loader2,
} from 'lucide-react';
import { Contract } from '../types';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import {
  OrchestratedProposalService,
  ReadinessReport,
  CollectedData,
  ContractData,
  NewDataInput,
  GenerationProgress,
  GeneratedProposal,
  AIFeedback,
  GenerationSession,
  GENERATION_PHASE_META,
  buildProposalTemplateRecord,
} from '../services/orchestrated-proposal-service';
import { GapResolutionPanel } from './proposal/GapResolutionPanel';
import { GenerationProgressPanel } from './proposal/GenerationProgress';
import { CheckpointGenerationFlow, CheckpointStatus } from './proposal/CheckpointGenerationFlow';
import toast from 'react-hot-toast';

// ============================================================================
// TYPES
// ============================================================================

interface OrchestratedProposalGeneratorProps {
  contract: Contract;
  onClose: () => void;
  onSuccess: (proposal: GeneratedProposal, collectedData: CollectedData, contract: ContractData) => void;
}

type Phase = 'analyzing' | 'gap-resolution' | 'generating' | 'checkpoint' | 'complete' | 'error';

// Feature flag: when on, the post-gap flow becomes the human-in-the-loop,
// checkpoint-gated, resumable flow instead of the legacy one-shot generation.
const CHECKPOINT_GENERATION = import.meta.env.VITE_CHECKPOINT_GENERATION === 'true';

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export function OrchestratedProposalGenerator({
  contract,
  onClose,
  onSuccess,
}: OrchestratedProposalGeneratorProps) {
  const { profile } = useAuth();

  // State
  const [phase, setPhase] = useState<Phase>('analyzing');
  const [readinessReport, setReadinessReport] = useState<ReadinessReport | null>(null);
  const [generationProgress, setGenerationProgress] = useState<GenerationProgress>({
    phase: 'analyzing',
    completedSections: [],
    totalSections: 8,
    percentage: 0,
    message: 'Initializing...'
  });
  const [generatedProposal, setGeneratedProposal] = useState<GeneratedProposal | null>(null);
  const [session, setSession] = useState<GenerationSession | null>(null);
  // Whether the active checkpoint session was entered via Resume (suppresses any
  // auto-run in the gated flow).
  const [checkpointIsResume, setCheckpointIsResume] = useState(false);
  // Latest status reported by the gated flow — drives the minimized sticky bar.
  const [checkpointStatus, setCheckpointStatus] = useState<CheckpointStatus | null>(null);
  // An active checkpoint session detected for this contract — offered as "resume".
  const [resumable, setResumable] = useState<GenerationSession | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  // Minimize-to-background + elapsed timer
  const [minimized, setMinimized] = useState(false);
  const [elapsedSec, setElapsedSec] = useState(0);
  const startRef = React.useRef<number>(Date.now());

  // Elapsed-time ticker — runs while analyzing/generating, freezes on complete/error.
  useEffect(() => {
    if (phase === 'complete' || phase === 'error') return;
    const t = setInterval(() => {
      setElapsedSec(Math.floor((Date.now() - startRef.current) / 1000));
    }, 1000);
    return () => clearInterval(t);
  }, [phase]);

  const formatElapsed = (s: number) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

  // While minimized, toast when a checkpoint gate finishes generating so the user
  // knows it's ready to review without expanding.
  const prevGeneratingRef = React.useRef(false);
  useEffect(() => {
    if (!checkpointStatus) return;
    const wasGenerating = prevGeneratingRef.current;
    prevGeneratingRef.current = checkpointStatus.generating;
    if (wasGenerating && !checkpointStatus.generating && minimized) {
      if (checkpointStatus.stopped) {
        toast.error(`${checkpointStatus.phaseLabel} needs your attention`);
      } else if (checkpointStatus.awaitingReview) {
        toast.success(`${checkpointStatus.phaseLabel} ready to review`);
      }
    }
  }, [checkpointStatus, minimized]);

  // Service instance with progress callback
  const serviceRef = React.useRef<OrchestratedProposalService | null>(null);

  useEffect(() => {
    serviceRef.current = new OrchestratedProposalService((progress) => {
      setGenerationProgress(progress);
    });
  }, []);

  // ============================================================================
  // INITIAL ANALYSIS
  // ============================================================================

  useEffect(() => {
    if (profile && phase === 'analyzing') {
      performInitialAnalysis();
    }
  }, [profile]);

  const performInitialAnalysis = async () => {
    if (!profile || !serviceRef.current) return;

    setIsLoading(true);
    setError(null);

    try {
      const contractId = contract.notice_id || contract.id || '';

      // Checkpoint flow: if an in-progress session exists for this contract, offer
      // to resume it instead of re-running the (expensive) readiness analysis.
      if (CHECKPOINT_GENERATION) {
        try {
          const existing = await serviceRef.current.loadSession(profile.id, contractId);
          if (existing && existing.status === 'active') {
            setResumable(existing);
            setIsLoading(false);
            return;
          }
        } catch (e) {
          console.warn('Resume check failed (continuing with fresh analysis):', e);
        }
      }

      const report = await serviceRef.current.analyzeReadiness(contractId, profile.id);

      setReadinessReport(report);
      setPhase('gap-resolution');

      if (report.overallScore >= 80 && report.gaps.critical.length === 0) {
        toast.success('Great! Your profile is well-prepared for this opportunity.');
      } else if (report.gaps.critical.length > 0) {
        toast('Review the gaps below to improve your proposal quality.', { icon: '📋' });
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Analysis failed';
      setError(message);
      setPhase('error');
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  // ============================================================================
  // ENRICHMENT (Add new data and re-analyze)
  // ============================================================================

  const handleEnrich = async (newData: NewDataInput) => {
    if (!profile || !serviceRef.current) return;

    setIsLoading(true);
    setError(null);

    try {
      const contractId = contract.notice_id || contract.id || '';
      const report = await serviceRef.current.enrichAndReanalyze(contractId, profile.id, newData);

      setReadinessReport(report);

      const improvement = report.overallScore - (readinessReport?.overallScore || 0);
      if (improvement > 0) {
        toast.success(`Score improved by ${improvement} points!`);
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Enrichment failed';
      setError(message);
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  // ============================================================================
  // GENERATION
  // ============================================================================

  const handleProceed = async (acknowledgedGaps: string[]) => {
    if (!profile || !serviceRef.current || !readinessReport) return;

    setError(null);

    // Checkpoint-gated path: create/resume a session and hand off to the gated
    // review flow. Each gate generates one step and waits for the user's approval.
    if (CHECKPOINT_GENERATION) {
      try {
        const noticeId = contract.notice_id || contract.id || '';
        const newSession = await serviceRef.current.createOrResumeSession({
          profileId: profile.id,
          noticeId,
          report: readinessReport,
          acknowledgedGaps,
        });
        setSession(newSession);
        setCheckpointIsResume(false);
        setPhase('checkpoint');
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Could not start generation';
        setError(message);
        setPhase('error');
        toast.error(message);
      }
      return;
    }

    setPhase('generating');

    try {
      const proposal = await serviceRef.current.generateProposal(
        readinessReport.collectedData,
        readinessReport.contract,
        acknowledgedGaps
      );

      setGeneratedProposal(proposal);

      // Store the proposal
      await storeProposal(proposal, readinessReport);

      toast.success('Proposal generated successfully!');

      // Directly notify parent and close - no intermediary success screen
      // This opens the ProposalDraftModal immediately with all content including AI Feedback
      onSuccess(proposal, readinessReport.collectedData, readinessReport.contract);
      onClose();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Generation failed';
      setError(message);
      setPhase('error');
      toast.error(message);
    }
  };

  // Resume an in-progress checkpoint session (skip re-analysis).
  const handleResume = () => {
    if (!resumable) return;
    setSession(resumable);
    setResumable(null);
    setCheckpointIsResume(true);
    setPhase('checkpoint');
  };

  // The gated flow abandoned the session (errored/interrupted gate the user chose
  // not to retry). It's already marked abandoned server-side; just close out.
  const handleCheckpointAbandoned = () => {
    toast('Generation abandoned');
    onClose();
  };

  // Discard the in-progress session and start a fresh analysis. The delete MUST
  // remove the row — otherwise the unique (profile_id, notice_id) row survives and
  // the next createOrResumeSession resumes the stale session instead of starting
  // clean. Surface a failure rather than silently continuing onto a stale resume.
  const handleStartOver = async () => {
    if (!profile || !serviceRef.current) return;
    const contractId = contract.notice_id || contract.id || '';
    try {
      const deleted = await serviceRef.current.deleteSession(profile.id, contractId);
      if (deleted === 0) {
        toast.error("Couldn't clear the previous draft — it may resume instead of starting fresh. Please retry.");
      }
    } catch (e: any) {
      console.warn('Could not delete prior session:', e);
      toast.error(`Couldn't clear the previous draft: ${e?.message || 'unknown error'}`);
    }
    setResumable(null);
    performInitialAnalysis();
  };

  // Called when the checkpoint flow finalizes (proposal already written to
  // proposal_templates by finalizeSession). Hand the assembled proposal to the
  // parent and close, matching the legacy success path.
  const handleCheckpointFinalized = (_templateId: string, proposal: GeneratedProposal) => {
    setGeneratedProposal(proposal);
    if (readinessReport) {
      onSuccess(proposal, readinessReport.collectedData, readinessReport.contract);
    }
    onClose();
  };

  // ============================================================================
  // STORE PROPOSAL
  // ============================================================================

  const storeProposal = async (proposal: GeneratedProposal, report: ReadinessReport) => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      console.error('Cannot store proposal: No authenticated user');
      return;
    }

    const noticeId = contract.notice_id || contract.id;

    // Delete existing proposal for this contract
    await supabase
      .from('proposal_templates')
      .delete()
      .eq('profile_id', user.id)
      .eq('notice_id', noticeId);

    // Shared with the checkpoint finalizeSession() path so both emit the exact
    // content_json shape required by proposal_templates' validation trigger.
    const proposalRecord = buildProposalTemplateRecord({
      proposal,
      profileId: user.id,
      noticeId,
      contractTitle: contract.title,
      overallScore: report.overallScore,
      gaps: report.gaps,
      matches: report.matches,
    });

    const { error } = await supabase
      .from('proposal_templates')
      .insert(proposalRecord);

    if (error) {
      console.error('Failed to store proposal:', error);
      toast.error('Failed to save proposal');
    } else {
      toast.success('Proposal saved');
    }
  };

  // ============================================================================
  // RENDER
  // ============================================================================

  // Minimized: a compact sticky bar. Crucially the modal itself is NOT unmounted
  // (see the render below) — it is only hidden — so a checkpoint gate that is mid
  // generation keeps running in the background instead of being aborted.
  const isCheckpointBar = phase === 'checkpoint' && !!checkpointStatus;
  const barGenerating = isCheckpointBar ? checkpointStatus!.generating : true;
  const barStopped = isCheckpointBar && checkpointStatus!.stopped;
  const barTitle = isCheckpointBar
    ? (barGenerating
        ? `Generating ${checkpointStatus!.phaseLabel}…`
        : barStopped
          ? `${checkpointStatus!.phaseLabel} needs attention`
          : `${checkpointStatus!.phaseLabel} ready`)
    : 'Generating proposal…';
  const barSubtitle = isCheckpointBar
    ? (barGenerating
        ? 'Working in the background…'
        : barStopped
          ? 'Expand to resume or abandon.'
          : 'Expand to review and approve.')
    : (generationProgress.message || 'Working…');
  const stickyBar = (
    <div className="fixed bottom-4 right-4 z-50 w-80 bg-bw-surface rounded-xl shadow-2xl border border-bw-border p-4">
      <div className="flex items-center gap-3">
        {barGenerating
          ? <Loader2 className="h-5 w-5 text-bw-primary animate-spin flex-shrink-0" />
          : barStopped
            ? <AlertCircle className="h-5 w-5 text-fit-moderate flex-shrink-0" />
            : <Sparkles className="h-5 w-5 text-success flex-shrink-0" />}
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between">
            <span className="text-sm font-semibold text-bw-ink truncate">{barTitle}</span>
            <span className="text-xs font-mono text-bw-muted ml-2">{formatElapsed(elapsedSec)}</span>
          </div>
          <p className="text-xs text-bw-muted truncate mt-0.5">{barSubtitle}</p>
          {!isCheckpointBar && (
            <div className="w-full bg-bw-surface-subtle rounded-full h-1.5 mt-2">
              <div className="bg-bw-primary h-1.5 rounded-full transition-all" style={{ width: `${Math.round(generationProgress.percentage || 0)}%` }} />
            </div>
          )}
        </div>
        <button
          onClick={() => setMinimized(false)}
          className="p-1.5 text-bw-muted hover:text-bw-ink hover:bg-bw-surface-subtle rounded-lg"
          title="Expand"
        >
          <Maximize2 className="h-4 w-4" />
        </button>
      </div>
    </div>
  );

  return (
    <>
    {minimized && stickyBar}
    <div className={`fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4 ${minimized ? 'hidden' : ''}`}>
      <div className="bg-bw-surface rounded-2xl shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex-shrink-0 px-6 py-4 border-b border-bw-border bg-bw-surface-subtle">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-3">
              {phase === 'generating' && (
                <button
                  onClick={() => setPhase('gap-resolution')}
                  disabled
                  className="p-2 text-bw-muted hover:text-bw-muted hover:bg-bw-surface-subtle rounded-lg transition-colors opacity-50 cursor-not-allowed"
                >
                  <ArrowLeft className="h-4 w-4" />
                </button>
              )}
              <div>
                <div className="flex items-center gap-2">
                  <Sparkles className="h-5 w-5 text-bw-primary" />
                  <h2 className="text-lg font-semibold text-bw-ink">Generate Proposal</h2>
                </div>
                <p className="text-sm text-bw-muted mt-0.5">{contract.title}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {(phase === 'analyzing' || phase === 'generating' || phase === 'checkpoint') && (
                <span className="inline-flex items-center gap-1 text-xs font-mono text-bw-muted bg-bw-surface-subtle px-2.5 py-1 rounded-md" title="Elapsed time">
                  <Clock className="h-3.5 w-3.5" /> {formatElapsed(elapsedSec)}
                </span>
              )}
              {(phase === 'generating' || phase === 'checkpoint') && (
                <button
                  onClick={() => setMinimized(true)}
                  className="p-2 text-bw-muted hover:text-bw-muted hover:bg-bw-surface-subtle rounded-lg transition-colors"
                  title="Run in background"
                >
                  <Minus className="h-5 w-5" />
                </button>
              )}
              <button
                onClick={onClose}
                className="p-2 text-bw-muted hover:text-bw-muted hover:bg-bw-surface-subtle rounded-lg transition-colors"
                disabled={phase === 'generating'}
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>

        {/* Step indicator (the checkpoint flow renders its own gate stepper) */}
        {phase !== 'error' && phase !== 'checkpoint' && (
          <div className="flex-shrink-0 px-6 py-3 border-b border-bw-border bg-bw-surface">
            <div className="flex items-center">
              {[
                { key: 'analyzing', label: 'Analyze' },
                { key: 'gap-resolution', label: 'Review & Resolve' },
                { key: 'generating', label: 'Generate' },
              ].map((step, idx) => {
                const order = ['analyzing', 'gap-resolution', 'generating'];
                const current = order.indexOf(phase === 'complete' ? 'generating' : phase);
                const state = idx < current ? 'done' : idx === current ? 'active' : 'upcoming';
                return (
                  <React.Fragment key={step.key}>
                    <div className="flex items-center gap-2">
                      <div
                        className={`flex items-center justify-center w-6 h-6 rounded-full text-xs font-semibold transition-colors ${
                          state === 'done'
                            ? 'bg-success text-white'
                            : state === 'active'
                            ? 'bg-bw-primary text-white'
                            : 'bg-bw-surface-subtle text-bw-muted'
                        }`}
                      >
                        {state === 'done' ? '✓' : idx + 1}
                      </div>
                      <span
                        className={`text-sm ${
                          state === 'active'
                            ? 'font-semibold text-bw-ink'
                            : state === 'done'
                            ? 'text-bw-muted'
                            : 'text-bw-muted'
                        }`}
                      >
                        {step.label}
                      </span>
                    </div>
                    {idx < 2 && (
                      <div className={`flex-1 h-px mx-3 ${idx < current ? 'bg-success' : 'bg-gray-200'}`} />
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Resume prompt — an in-progress checkpoint session exists */}
          {phase === 'analyzing' && resumable && (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <div className="w-14 h-14 bg-bw-primary-tint rounded-full flex items-center justify-center mb-4">
                <Sparkles className="w-7 h-7 text-bw-primary" />
              </div>
              <h3 className="text-lg font-semibold text-bw-ink mb-1">Resume your generation?</h3>
              <p className="text-sm text-bw-muted max-w-md mb-1">
                You have an in-progress proposal for this contract, currently at{' '}
                <span className="font-medium text-bw-ink">
                  {resumable.currentPhase === 'finalize'
                    ? 'Finalize'
                    : GENERATION_PHASE_META[resumable.currentPhase]?.label || resumable.currentPhase}
                </span>.
              </p>
              <p className="text-xs text-bw-muted max-w-md mb-6">
                Resume picks up exactly where you left off. Starting over discards the in-progress work.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={handleStartOver}
                  className="px-4 py-2 text-bw-ink bg-bw-surface border border-bw-border rounded-lg hover:bg-bw-surface-subtle transition-colors text-sm font-medium"
                >
                  Start over
                </button>
                <button
                  onClick={handleResume}
                  className="px-5 py-2 bg-bw-primary text-white rounded-lg hover:brightness-110 transition-colors text-sm font-medium"
                >
                  Resume
                </button>
              </div>
            </div>
          )}

          {/* Analyzing Phase */}
          {phase === 'analyzing' && !resumable && (
            <div className="flex flex-col items-center justify-center py-16">
              <div className="relative">
                <div className="w-16 h-16 rounded-full border-2 border-bw-border" />
                <div className="absolute inset-0 w-16 h-16 rounded-full border-2 border-blue-600 border-t-transparent animate-spin" />
              </div>
              <p className="text-bw-muted mt-4 font-medium">Reading the solicitation and your profile…</p>
              <p className="text-sm text-bw-muted mt-1 max-w-md text-center">
                Extracting the governing rules — vehicle, evaluation factors, submission volumes,
                PWS tasks, and required key personnel — and matching them to your company data.
              </p>
            </div>
          )}

          {/* Gap Resolution Phase */}
          {phase === 'gap-resolution' && readinessReport && (
            <GapResolutionPanel
              readinessReport={readinessReport}
              onEnrich={handleEnrich}
              onProceed={handleProceed}
              onCancel={onClose}
              isLoading={isLoading}
            />
          )}

          {/* Generating Phase (legacy one-shot) */}
          {phase === 'generating' && (
            <GenerationProgressPanel progress={generationProgress} />
          )}

          {/* Checkpoint-gated, human-in-the-loop flow */}
          {phase === 'checkpoint' && session && serviceRef.current && (
            <CheckpointGenerationFlow
              service={serviceRef.current}
              initialSession={session}
              isResume={checkpointIsResume}
              onFinalized={handleCheckpointFinalized}
              onAbandon={handleCheckpointAbandoned}
              onStatusChange={setCheckpointStatus}
            />
          )}

          {/* Error Phase */}
          {phase === 'error' && (
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-urgent-bg rounded-full flex items-center justify-center mx-auto mb-4">
                <AlertCircle className="w-8 h-8 text-urgent" />
              </div>
              <h3 className="text-xl font-semibold text-bw-ink mb-2">
                Something Went Wrong
              </h3>
              <p className="text-bw-muted mb-2">{error}</p>
              <div className="flex justify-center gap-3 mt-6">
                <button
                  onClick={onClose}
                  className="px-4 py-2 text-bw-ink bg-bw-surface border border-bw-border rounded-lg hover:bg-bw-surface-subtle transition-colors font-medium"
                >
                  Close
                </button>
                <button
                  onClick={performInitialAnalysis}
                  className="px-4 py-2 text-white bg-slate-800 rounded-lg hover:bg-slate-700 transition-colors font-medium"
                >
                  Try Again
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
    </>
  );
}

export default OrchestratedProposalGenerator;
